import {
  DatePreset,
  FilterControlType,
  type DatePresetOption,
  type FilterDefinition,
  type MultiSelectOption,
} from 'corso-admin-ui';
import {
  CrewMerchantUi,
  OasWebhookTopic,
  type ViewDateValue,
} from 'corso-types';
import { computeDateFilterValue } from 'corso-web-lib';

/**
 * Dates are held as the shared `ViewDateValue` so a preset stays a preset, and are resolved to the
 * concrete range the endpoint expects only when the request is built.
 */
export type DeliveryFilterValues = Omit<
  CrewMerchantUi.ConfigWebhookDeliveryFilters,
  'createdOn'
> & { createdOn?: ViewDateValue };

export type DeliveryFilterId = keyof DeliveryFilterValues;

/** Adding a filter id to the zod schema fails the build here until it is classified. */
const filterIdToControlType = {
  statuses: FilterControlType.multiselect,
  topics: FilterControlType.multiselect,
  createdOn: FilterControlType.date,
  responseBuckets: FilterControlType.multiselect,
} as const satisfies Record<DeliveryFilterId, FilterControlType>;

type DeliveryFilterControlType = typeof filterIdToControlType;

type Options = {
  [FilterControlType.multiselect]: MultiSelectOption[];
  [FilterControlType.date]: readonly DatePresetOption[];
  [FilterControlType.boolean]: never;
  [FilterControlType.text]: never;
};

export type DeliveryFilter<ID extends DeliveryFilterId = DeliveryFilterId> = {
  [K in ID]: {
    id: K;
    label: string;
    verb?: string;
    value?: DeliveryFilterValues[K];
    type: DeliveryFilterControlType[K];
    options: Options[DeliveryFilterControlType[K]];
  };
}[ID];

export type DeliveryFilterDefinition = {
  [ID in DeliveryFilterId]: DeliveryFilter<ID>;
};

/**
 * Capped at the endpoint's retention window — a wider preset would be silently clamped, so it
 * is not offered.
 */
const dateOptions = [
  { value: DatePreset.today, label: 'Today' },
  { value: DatePreset.last7Days, label: 'Last 7 Days' },
  { value: DatePreset.last30Days, label: 'Last 30 Days' },
  { value: DatePreset.last90Days, label: 'Last 90 Days' },
  { value: DatePreset.custom, label: 'Custom' },
] as const satisfies Options[typeof FilterControlType.date];

/** The topic the merchant's endpoint actually receives, matching the table's Topic column. */
const topicLabels = {
  Claim_Created: OasWebhookTopic.claimCreated,
  Claim_Updated: OasWebhookTopic.claimUpdated,
  Claim_Finalized: OasWebhookTopic.claimFinalized,
  Shipping_Claim_Created: OasWebhookTopic.shippingClaimCreated,
  Shipping_Claim_Updated: OasWebhookTopic.shippingClaimUpdated,
  Shipping_Claim_Finalized: OasWebhookTopic.shippingClaimFinalized,
  Registration_Created: OasWebhookTopic.registrationCreated,
} as const satisfies Record<CrewMerchantUi.ConfigWebhookTopic, OasWebhookTopic>;

const responseBucketLabels = {
  '2xx': 'Success (2xx)',
  '3xx': 'Redirect (3xx)',
  '4xx': 'Client error (4xx)',
  '5xx': 'Server error (5xx)',
  none: 'No response',
} as const satisfies Record<
  CrewMerchantUi.ConfigWebhookDeliveryResponseBucket,
  string
>;

export const deliveryFilterDefinition = {
  statuses: {
    id: 'statuses',
    label: 'Status',
    verb: 'is',
    type: FilterControlType.multiselect,
    options: Object.values(CrewMerchantUi.ConfigWebhookDeliveryStatus).map(
      (status) => ({ value: status, label: status }),
    ),
  },
  topics: {
    id: 'topics',
    label: 'Topic',
    verb: 'is',
    type: FilterControlType.multiselect,
    options: Object.values(CrewMerchantUi.ConfigWebhookTopic).map((topic) => ({
      value: topic,
      label: topicLabels[topic],
    })),
  },
  responseBuckets: {
    id: 'responseBuckets',
    label: 'Response',
    verb: 'is',
    type: FilterControlType.multiselect,
    options: Object.values(
      CrewMerchantUi.ConfigWebhookDeliveryResponseBucket,
    ).map((bucket) => ({
      value: bucket,
      label: responseBucketLabels[bucket],
    })),
  },
  createdOn: {
    id: 'createdOn',
    label: 'Date',
    verb: 'is',
    type: FilterControlType.date,
    options: dateOptions,
  },
} as const satisfies DeliveryFilterDefinition &
  Record<DeliveryFilterId, FilterDefinition>;

export const toRequestFilters = ({
  createdOn,
  ...rest
}: DeliveryFilterValues) =>
  ({
    ...rest,
    ...(createdOn && { createdOn: computeDateFilterValue(createdOn) }),
  }) satisfies CrewMerchantUi.ConfigWebhookDeliveryFilters;
